import React, { memo, useCallback } from 'react';
import BulletinCard from './BulletinCard';
import BulletinsInfiniteLoader from './BulletinsInfiniteLoader';
import { useBulletinGridLayout } from '@/hooks/use-bulletin-grid-layout';
import { RelicWithProfile } from '@/hooks/use-infinite-relics';

interface RelicsGridProps {
  relics: RelicWithProfile[];
  onLike: (id: string) => void;
  onDelete: (id: string) => void;
  activeTab: 'pour-toi' | 'updates';
  onLoadMore: () => void;
  isLoading: boolean;
  hasNextPage: boolean;
  onRelicClick: (relicId: string) => void;
  selectedRelicId?: string | null;
}

const RelicsGrid: React.FC<RelicsGridProps> = memo(({
  relics,
  onLike,
  onDelete,
  activeTab,
  onLoadMore,
  isLoading,
  hasNextPage,
  onRelicClick,
  selectedRelicId
}) => {
  console.log('🔮 [RELICS GRID] Rendering with', relics.length, 'relics');
  
  const { gridItems, getColumnClass, isMobile } = useBulletinGridLayout({ bulletins: relics });

  const handleRelicClick = useCallback((relicId: string) => {
    console.log('🔮 [RELICS GRID] Relic clicked:', relicId);
    onRelicClick(relicId);
  }, [onRelicClick]);

  if (!relics || relics.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-6xl mb-4">🏺</div>
        <h3 className="text-lg font-medium text-foreground mb-2">No Relics Found</h3>
        <p className="text-muted-foreground">
          Check back later as new relics are discovered and added to the collection.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className={`grid gap-4 ${getColumnClass()}`}>
        {relics.map((relic, index) => {
          const gridSize = gridItems[index % gridItems.length] || gridItems[0];
          
          return (
            <div
              key={`${relic.id}-${index}`}
              className={`
                ${gridSize?.spans?.col === 2 ? 'col-span-2' : 'col-span-1'}
                ${gridSize?.spans?.row === 2 ? 'row-span-2' : 'row-span-1'}
                transition-all duration-200 hover:scale-[1.02]
                border border-border/50 hover:border-border
                bg-card/80 hover:bg-card
                backdrop-blur-sm
                cursor-pointer
              `}
              onClick={() => handleRelicClick(relic.id)}
            >
              <BulletinCard
                bulletin={relic}
                size={gridSize?.spans?.isHorizontal ? 'horizontal' : gridSize?.spans?.row === 2 ? 'large' : 'medium'}
                onLike={onLike}
                onDelete={() => onDelete(relic.id)}
                isSelected={selectedRelicId === relic.id}
              />
            </div>
          );
        })}
      </div>
      
      <BulletinsInfiniteLoader
        onLoadMore={() => onLoadMore()}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
      />
    </div>
  );
});

RelicsGrid.displayName = 'RelicsGrid';

export default RelicsGrid;